import { useEffect, useState, type ReactNode } from "react";

// ─── Generic dropdown primitives ───────────────────────────────────────────
// Ported from the Market page's filter toolbar so the Fresh page (and any
// future filter bar) shares one compact pill-dropdown look. Market keeps its
// own copies for now; this module is the reusable home going forward.

/** Pill-shaped trigger button. Active state = accent background so applied
 *  filters are spottable at a glance. */
export function TriggerButton({
  active,
  onClick,
  children,
  open,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  open?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors ${
        active
          ? "bg-accent-500/15 text-accent-100 border border-accent-500/40 shadow-sm shadow-accent-500/10"
          : "bg-ink-900/60 text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100"
      }`}
    >
      <span>{children}</span>
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`transition-transform ${open ? "rotate-180" : "rotate-0"} ${
          active ? "opacity-80" : "opacity-50"
        }`}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
}

/** Generic popover. Click trigger to open; click outside or Escape closes. */
export function Popover({
  open,
  onClose,
  children,
  align = "left",
  width,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  align?: "left" | "right";
  width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-[1100]" onClick={onClose} />
      <div
        className={`absolute z-[1200] mt-1 ${
          align === "right" ? "right-0" : "left-0"
        } ${width ?? "min-w-[14rem]"} rounded-lg bg-ink-900 border border-white/10 shadow-xl p-3`}
      >
        {children}
      </div>
    </>
  );
}

/** One option button inside a popover; works for both radio (single) and
 *  checkbox (multi) usage. */
export function MenuItem({
  checked,
  onClick,
  children,
}: {
  checked: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-[12px] text-left transition-colors ${
        checked
          ? "bg-accent-500/15 text-accent-100"
          : "text-slate-300 hover:bg-white/[0.04] hover:text-slate-100"
      }`}
    >
      <span className="truncate">{children}</span>
      {checked && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-accent-300 flex-shrink-0"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </button>
  );
}

// ─── Composed filter dropdowns ─────────────────────────────────────────────

export interface FacetOption {
  value: string;
  count?: number;
  /** Optional human-friendly label. When set it's shown (and searched)
   *  instead of the raw ``value`` — used by pages whose facet codes are
   *  enum-ish (``new_built``) and want a readable caption. Facet-driven
   *  callers (Fresh) omit it and fall back to ``value``. */
  label?: string;
}

/** Multi-select dropdown over a facet list (value + optional count). Several
 *  picks within one filter are OR'd server-side. Shows an inline search box
 *  once the option list gets long. */
export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  align = "left",
  hint,
}: {
  label: string;
  options: FacetOption[] | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
  align?: "left" | "right";
  /** Optional note under the option list, for when what an option *means* is
   *  not obvious from its label. */
  hint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const opts = options ?? [];
  const searchable = opts.length > 8;
  const visible = searchable
    ? opts.filter((o) =>
        (o.label ?? o.value).toLowerCase().includes(query.toLowerCase()),
      )
    : opts;

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <div className="relative">
      <TriggerButton active={selected.length > 0} open={open} onClick={() => setOpen((o) => !o)}>
        {label}
        {selected.length > 0 && ` (${selected.length})`}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)} align={align} width="min-w-[15rem]">
        {searchable && (
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            className="input !py-1.5 !text-[12px] mb-2"
            autoFocus
          />
        )}
        <div className="max-h-64 overflow-y-auto space-y-0.5 pr-0.5">
          {visible.length === 0 && (
            <div className="px-2.5 py-2 text-[12px] text-slate-500">No options</div>
          )}
          {visible.map((o) => (
            <MenuItem key={o.value} checked={selected.includes(o.value)} onClick={() => toggle(o.value)}>
              {o.label ?? o.value}
              {o.count != null && <span className="text-slate-500"> ({o.count})</span>}
            </MenuItem>
          ))}
        </div>
        {hint && (
          <p className="mt-2 px-1 text-[11px] leading-snug text-slate-500">{hint}</p>
        )}
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="mt-2 w-full text-[11px] text-slate-400 hover:text-slate-200 py-1"
          >
            Clear {label.toLowerCase()}
          </button>
        )}
      </Popover>
    </div>
  );
}

export interface RangePreset {
  label: string;
  min?: string;
  max?: string;
}

/** Min/max numeric range dropdown with optional quick-pick presets. ``topSlot``
 *  renders above the inputs (used for the price ₾/$ toggle). */
export function RangeDropdown({
  label,
  triggerLabel,
  active,
  min,
  max,
  onMin,
  onMax,
  prefix,
  suffix,
  presets,
  topSlot,
  align = "left",
}: {
  label: string;
  triggerLabel?: string;
  active: boolean;
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
  prefix?: string;
  suffix?: string;
  presets?: RangePreset[];
  topSlot?: ReactNode;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <TriggerButton active={active} open={open} onClick={() => setOpen((o) => !o)}>
        {active && triggerLabel ? triggerLabel : label}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)} align={align} width="min-w-[16rem]">
        {topSlot && <div className="mb-2">{topSlot}</div>}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            {prefix && (
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-slate-500">
                {prefix}
              </span>
            )}
            <input
              value={min}
              onChange={(e) => onMin(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="Min"
              inputMode="numeric"
              className={`input !py-1.5 !text-[12px] ${prefix ? "!pl-6" : ""}`}
            />
          </div>
          <span className="text-slate-600">–</span>
          <div className="relative flex-1">
            {prefix && (
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-slate-500">
                {prefix}
              </span>
            )}
            <input
              value={max}
              onChange={(e) => onMax(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="Max"
              inputMode="numeric"
              className={`input !py-1.5 !text-[12px] ${prefix ? "!pl-6" : ""}`}
            />
          </div>
          {suffix && <span className="text-[12px] text-slate-500">{suffix}</span>}
        </div>
        {presets && presets.length > 0 && (
          <div className="grid grid-cols-2 gap-1.5 mt-2.5">
            {presets.map((p) => {
              const isActive = (p.min ?? "") === min && (p.max ?? "") === max;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    onMin(p.min ?? "");
                    onMax(p.max ?? "");
                  }}
                  className={`h-8 rounded-md text-[11px] font-medium border transition ${
                    isActive
                      ? "bg-accent-500/15 text-accent-100 border-accent-500/40"
                      : "border-white/10 text-slate-300 hover:border-white/20 hover:text-slate-100"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        )}
        {active && (
          <button
            type="button"
            onClick={() => {
              onMin("");
              onMax("");
            }}
            className="mt-2 w-full text-[11px] text-slate-400 hover:text-slate-200 py-1"
          >
            Clear
          </button>
        )}
      </Popover>
    </div>
  );
}

/** Single-select pill dropdown (e.g. sort, recency). */
export function SingleSelectDropdown({
  label,
  value,
  options,
  onChange,
  align = "left",
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <div className="relative">
      <TriggerButton active={false} open={open} onClick={() => setOpen((o) => !o)}>
        {label}
        {current ? `: ${current.label}` : ""}
      </TriggerButton>
      <Popover open={open} onClose={() => setOpen(false)} align={align}>
        <div className="space-y-0.5">
          {options.map((o) => (
            <MenuItem
              key={o.value}
              checked={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </MenuItem>
          ))}
        </div>
      </Popover>
    </div>
  );
}

/** Inline min/max range (no popover) — for use inside a larger panel like a
 *  "More filters" popover. */
export function InlineRange({
  min,
  max,
  onMin,
  onMax,
  prefix,
  suffix,
}: {
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
  prefix?: string;
  suffix?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        value={min}
        onChange={(e) => onMin(e.target.value.replace(/[^\d]/g, ""))}
        placeholder={prefix ? `${prefix} min` : "Min"}
        inputMode="numeric"
        className="input !py-1.5 !text-[12px]"
      />
      <span className="text-slate-600">–</span>
      <input
        value={max}
        onChange={(e) => onMax(e.target.value.replace(/[^\d]/g, ""))}
        placeholder={prefix ? `${prefix} max` : "Max"}
        inputMode="numeric"
        className="input !py-1.5 !text-[12px]"
      />
      {suffix && <span className="text-[12px] text-slate-500">{suffix}</span>}
    </div>
  );
}

/** Inline multi-select checklist (no popover) — for use inside a larger panel.
 *  Adds a search box once the list gets long. */
export function InlineMultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: FacetOption[] | undefined;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const opts = options ?? [];
  const searchable = opts.length > 6;
  const visible = searchable
    ? opts.filter((o) =>
        (o.label ?? o.value).toLowerCase().includes(query.toLowerCase()),
      )
    : opts;
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div>
      {searchable && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}…`}
          className="input !py-1.5 !text-[12px] mb-1.5"
        />
      )}
      <div className="max-h-40 overflow-y-auto space-y-0.5 pr-0.5">
        {visible.length === 0 && (
          <div className="px-2.5 py-1.5 text-[12px] text-slate-500">No options</div>
        )}
        {visible.map((o) => (
          <MenuItem key={o.value} checked={selected.includes(o.value)} onClick={() => toggle(o.value)}>
            {o.label ?? o.value}
            {o.count != null && <span className="text-slate-500"> ({o.count})</span>}
          </MenuItem>
        ))}
      </div>
    </div>
  );
}

/** Segmented 2–3 way toggle for a small exclusive choice (source, audience,
 *  platform). Lives in the toolbar next to the Filters button. */
export function SegToggle({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex h-9 rounded-lg border border-white/10 bg-ink-900/60 p-0.5">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`px-3 h-8 rounded-md text-[12px] font-semibold transition-colors ${
              active
                ? "bg-accent-500/20 text-accent-100 border border-accent-500/40"
                : "text-slate-400 hover:text-slate-100"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Preset quick-pick chips for a numeric range (rooms / price / area). Drives
 *  the same min/max setters the range inputs use so presets and manual entry
 *  stay in sync. */
export function PresetChips({
  presets,
  min,
  max,
  onMin,
  onMax,
}: {
  presets: RangePreset[];
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {presets.map((p) => {
        const active = (p.min ?? "") === min && (p.max ?? "") === max;
        return (
          <button
            key={p.label}
            type="button"
            onClick={() => {
              onMin(p.min ?? "");
              onMax(p.max ?? "");
            }}
            className={`h-9 rounded-lg text-[11px] font-medium border transition ${
              active
                ? "bg-accent-500/15 text-accent-100 border-accent-500/40"
                : "border-white/10 text-slate-300 hover:border-white/20 hover:text-slate-100"
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

/** One labelled section in a Filters drawer. */
export function FilterSection({
  title,
  children,
  aside,
}: {
  title: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="py-4 border-b border-white/5 last:border-0 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {title}
        </h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** A single toggle chip — the shared look for "one of many" quick picks
 *  (property type, rooms, recency) inside a filter panel. */
export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 px-3 rounded-lg text-[12px] font-medium border transition ${
        active
          ? "bg-accent-500/15 text-accent-100 border-accent-500/40"
          : "border-white/10 text-slate-300 hover:border-white/20 hover:text-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

export interface ActiveChip {
  key: string;
  label: string;
  onRemove: () => void;
}

/** Removable chips for the currently-applied filters, with a clear-all. Sits
 *  directly under the toolbar so the active filter set is readable without
 *  opening the drawer. */
export function FilterChips({
  chips,
  onClearAll,
}: {
  chips: ActiveChip[];
  onClearAll: () => void;
}) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-4">
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={c.onRemove}
          className="group inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full text-[11px] font-medium bg-accent-500/10 border border-accent-500/30 text-accent-100 hover:bg-accent-500/20 transition"
        >
          <span className="max-w-[12rem] truncate">{c.label}</span>
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            className="opacity-60 group-hover:opacity-100"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="h-7 px-2.5 rounded-full text-[11px] font-medium text-slate-400 hover:text-slate-100 transition"
      >
        Clear all
      </button>
    </div>
  );
}

/** A row of labelled checkboxes for boolean flag filters. */
export function ToggleRow({
  items,
}: {
  items: { label: string; checked: boolean; onChange: (v: boolean) => void }[];
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {items.map((it) => (
        <MenuItem key={it.label} checked={it.checked} onClick={() => it.onChange(!it.checked)}>
          {it.label}
        </MenuItem>
      ))}
    </div>
  );
}
